import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ticket } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Pill, inputCls } from "@/components/hub/Common";
import { PromoStats, PromoStatsButton } from "@/components/admin/PromoStats";

const local = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16); };
const EMPTY = { code: "", bonus_small: 3, bonus_medium: 10, bonus_large: 35, max_uses: 100, ends_at: "", active: true, kind: "bonus", gift_clips: 5, prizes: [] };

const Num = ({ label, value, onChange, testid, min = 0 }) => (
  <label className="block">
    <span className="text-[11px] text-slate-400">{label}</span>
    <input type="number" min={min} className={`${inputCls} mt-1`} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} />
  </label>
);

const GiftFields = ({ f, set, t }) => {
  const game = f.prizes.length > 0;
  const setPrize = (i) => (v) => set("prizes")(f.prizes.map((x, j) => (j === i ? v : x)));
  return (
    <div className="space-y-3">
      <button type="button" onClick={() => set("prizes")(game ? [] : [10, 20, 30])} aria-pressed={game} data-testid="promo-admin-game-toggle"
        className={`cx-metal rounded-full px-4 py-2 text-xs ${game ? "cx-metal-active" : ""}`}>🎴 {t("adm.pr_game")}</button>
      {game ? <div className="grid grid-cols-3 gap-3">{f.prizes.map((v, i) => <Num key={i} label={`${t("adm.pr_prizes")} ${i + 1}`} value={v} onChange={setPrize(i)} testid={`promo-admin-prize-${i}`} min={1} />)}</div>
        : <Num label={t("adm.pr_gift_clips")} value={f.gift_clips} onChange={set("gift_clips")} testid="promo-admin-gift-clips" min={1} />}
    </div>
  );
};

const Form = ({ initial, id, onDone, onCancel }) => {
  const { t } = useI18n();
  const [f, setF] = useState(initial);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null };
    try { if (id) await api.put(`/admin/promos/${id}`, body); else await api.post("/admin/promos", body); toast.success(t("adm.pr_saved")); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="cx-card cx-brackets rounded-2xl p-6 space-y-4" data-testid="promo-admin-form">
      <div className="font-display text-lg text-white">{id ? f.code : t("adm.pr_new")}</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <label className="block">
          <span className="text-[11px] text-slate-400">{t("adm.pr_code")}</span>
          <input required minLength={3} maxLength={24} className={`${inputCls} mt-1 font-mono uppercase`} value={f.code} placeholder="LANCEMENT"
            onChange={(e) => set("code")(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))} data-testid="promo-admin-code" />
        </label>
        <button type="button" onClick={() => set("active")(!f.active)} data-testid="promo-admin-active" aria-pressed={f.active}
          className={`cx-metal rounded-full px-4 py-2.5 text-xs font-semibold ${f.active ? "cx-metal-active" : ""}`}>{f.active ? t("adm.pr_active") : t("adm.pr_inactive")}</button>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="promo-admin-kind">
        <span className="text-[11px] text-slate-400 mr-1">{t("adm.pr_kind")}</span>
        {["bonus", "gift"].map((k) => (
          <button key={k} type="button" onClick={() => set("kind")(k)} data-testid={`promo-admin-kind-${k}`} className={`cx-metal rounded-full px-4 py-2 text-xs ${f.kind === k ? "cx-metal-active" : ""}`}>{t(`adm.pr_kind_${k}`)}</button>
        ))}
      </div>
      {f.kind === "gift" ? <GiftFields f={f} set={set} t={t} /> : <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Num label={t("adm.pr_bonus_small")} value={f.bonus_small} onChange={set("bonus_small")} testid="promo-admin-bonus-small" />
        <Num label={t("adm.pr_bonus_medium")} value={f.bonus_medium} onChange={set("bonus_medium")} testid="promo-admin-bonus-medium" />
        <Num label={t("adm.pr_bonus_large")} value={f.bonus_large} onChange={set("bonus_large")} testid="promo-admin-bonus-large" />
      </div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Num label={t("adm.pr_max")} value={f.max_uses} onChange={set("max_uses")} testid="promo-admin-max" />
        <label className="block">
          <span className="text-[11px] text-slate-400">{t("adm.pr_ends")}</span>
          <input type="datetime-local" className={`${inputCls} mt-1`} value={f.ends_at} onChange={(e) => set("ends_at")(e.target.value)} data-testid="promo-admin-ends" />
        </label>
      </div>
      <div className="flex gap-2">
        <button className="cx-btn rounded-full px-6 py-2.5 text-sm font-semibold" data-testid="promo-admin-save">{t("adm.pr_save")}</button>
        <button type="button" onClick={onCancel} className="cx-metal rounded-full px-5 py-2.5 text-sm" data-testid="promo-admin-cancel">{t("adm.pr_cancel")}</button>
      </div>
    </form>
  );
};

const Row = ({ p, onEdit, onDelete }) => {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(false);
  const done = p.uses >= p.max_uses || (p.ends_at && new Date(p.ends_at) < new Date());
  return (
    <div className="cx-card rounded-xl px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`promo-row-${p.code}`}>
      <Ticket className="w-5 h-5 text-[#ffc94d]" />
      <span className="font-mono text-white font-bold">{p.code}</span>
      <Pill tone={!p.active || done ? "muted" : "green"}>{!p.active ? t("adm.pr_inactive") : done ? t("adm.pr_used") : t("adm.pr_active")}</Pill>
      {p.kind === "gift" ? <span className="text-xs text-[#22e3a1] font-mono" data-testid={`promo-gift-${p.code}`}>{p.prizes?.length ? `🎴 ${t("adm.pr_game_row").replace("{p}", p.prizes.join(" / "))}` : t("adm.pr_gift_row").replace("{n}", p.gift_clips)}</span>
        : <span className="text-xs text-slate-400 font-mono">+{p.bonus.small} / +{p.bonus.medium} / +{p.bonus.large}</span>}
      <span className="text-xs text-slate-400" data-testid={`promo-uses-${p.code}`}>{t("adm.pr_uses").replace("{u}", p.uses).replace("{m}", p.max_uses)}</span>
      <span className="text-xs text-slate-500">{p.ends_at ? t("adm.pr_until").replace("{d}", new Date(p.ends_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")) : t("adm.pr_no_end")}</span>
      <div className="ml-auto flex gap-2">
        <PromoStatsButton open={stats} onClick={() => setStats((x) => !x)} code={p.code} />
        <button onClick={onEdit} className="cx-metal rounded-lg p-2" data-testid={`promo-edit-${p.code}`}><Pencil className="w-4 h-4" /></button>
        <button onClick={onDelete} className="cx-metal rounded-lg p-2 !text-rose-300" data-testid={`promo-delete-${p.code}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {stats && <PromoStats code={p.code} />}
    </div>
  );
};

import { GiftCardDialog, GiftCardMaker } from "@/components/admin/GiftCard";

export const PromosTab = () => {
  const { t } = useI18n();
  const [card, setCard] = useState(null);
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/promos").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (p) => {
    if (!window.confirm(t("adm.pr_delete_confirm"))) return;
    try { await api.delete(`/admin/promos/${p.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const done = () => { setEdit(null); load(); };
  return (
    <div className="space-y-4" data-testid="admin-promos">
      <GiftCardDialog card={card} onClose={() => setCard(null)} />
      <GiftCardMaker onCreated={(c) => { setCard(c); load(); }} />
      {edit ? <Form key={edit.id || "new"} id={edit.id} initial={edit.f} onDone={done} onCancel={() => setEdit(null)} />
        : <button onClick={() => setEdit({ f: EMPTY })} className="cx-btn rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2" data-testid="promo-new-btn"><Plus className="w-4 h-4" />{t("adm.pr_new")}</button>}
      {!list.length && <p className="text-sm text-slate-500">{t("adm.pr_empty")}</p>}
      {list.map((p) => (
        <Row key={p.id} p={p} onDelete={() => del(p)} onEdit={() => setEdit({ id: p.id, f: { code: p.code, bonus_small: p.bonus.small, bonus_medium: p.bonus.medium, bonus_large: p.bonus.large, max_uses: p.max_uses, ends_at: p.ends_at ? local(p.ends_at) : "", active: p.active, kind: p.kind || "bonus", gift_clips: p.gift_clips || 5, prizes: p.prizes || [] } })} />
      ))}
    </div>
  );
};
